import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ProductCard from "./ProductCard";
import ViewAllButton from "./ViewAllButton";
import { sortProductsAlphanumeric } from "@shared/lib";
import "./ProductSection.css";

interface Product {
  id: number;
  name: string;
  categories: { id: number; name: string; parent: number }[];
  images: { src: string }[];
}

const ProductsSection: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const sliderRef = useRef<Slider>(null);
  
  const API_URL_PRODUCTS = "https://huter.diy/wordpress/wp-json/wc/v3/products";
  const API_KEY = 'ck_b633c1877cf1de3bdbb01117e1fa8215b9b99e43';
  const API_SECRET = 'cs_5c69080f905a13fda5aefffb41f6ac3988ab6478';

  const navigate = useNavigate();

  const nextSlide = () => {
    sliderRef.current?.slickNext();
  };

  const prevSlide = () => {
    sliderRef.current?.slickPrev();
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        // Загружаем недавно добавленные товары, сортируя по дате создания
        const response = await fetch(`${API_URL_PRODUCTS}?per_page=12&orderby=date&order=desc&consumer_key=${API_KEY}&consumer_secret=${API_SECRET}`);
        const data: Product[] = await response.json();
        // Применяем алфавитно-цифровую сортировку к товарам
        const sortedProducts = sortProductsAlphanumeric(data);
        setProducts(sortedProducts);
      } catch (error) {
        console.error("Error loading products:", error);
      }
    };

    fetchProducts();
  }, []);

  const handleShowAllClick = () => {
    navigate("/catalog");
    // Прокрутка страницы вверх при переходе на каталог
    window.scrollTo(0, 0);
  };

  const handleProductClick = (productId: number) => {
    navigate(`/product/${productId}`);
  };

  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    arrows: false,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 4,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
        },
      },
    ],
  };

  return (
    <div className="flex flex-col py-0 mx-auto my-0 max-w-[1290px]">
      <h1 className="self-start mb-32 text-6xl font-bold text-black max-md:text-5xl max-sm:text-4xl bg-yellow-400 px-12 py-7 rounded-2xl w-fit">
      Our products
      </h1>
      <div className="flex flex-col md:items-start items-center gap-4 w-full max-sm:w-full">
        <button className="w-[283px]" onClick={handleShowAllClick}>
          <ViewAllButton />
        </button>
        
        {/* Navigation Buttons */}
        <div className="flex gap-3 w-full justify-between">
          <button
            onClick={prevSlide}
            className="bg-yellow-400 hover:bg-yellow-500 transition-colors p-2 rounded-full transform"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18L9 12L15 6" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            onClick={nextSlide}
            className="bg-yellow-400 hover:bg-yellow-500 transition-colors p-2 rounded-full transform"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 18L15 12L9 6" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </div>

      <div className="relative mt-[35px]">
        {products.length > 0 ? (
          <Slider ref={sliderRef} {...sliderSettings} className="w-full">
            {products.map((product) => (
              product.categories && product.categories.length > 0 && product.categories[0].name !== "Misc" ?
              <div key={product.id} className="px-2">
                <div onClick={() => handleProductClick(product.id)} className="cursor-pointer">
                  <ProductCard
                    image={product.images[0]?.src || "/placeholder.jpg"}
                    title={product.name}
                    category={product.categories.map((cat) => cat.name).join(", ")}
                  />
                </div>
              </div> : null
            ))}
          </Slider>
        ) : (
          <p>Loading products...</p>
        )}
      </div>
    </div>
  );
};

export default ProductsSection;
